/**
 * MONSTER TOWN — THE MAXED-TOWN STRESS FIXTURE. OWNED BY PERF (src/perf/**).
 *
 * A KID WILL FILL THE PLOT. Every perf number taken against the demo layout
 * (eleven objects) is a number about a game nobody plays. This builds the worst
 * case a save file can legally contain and does it deterministically, so the
 * budget is measured against the state the game will actually be in six weeks
 * after a kid gets it:
 *
 *   - the town plot filled to `townFill` of its walkable cells with real
 *     placeables (paths, fences, trees, flowers, benches, lamp posts, blocks),
 *     stacked two levels deep where the pieces allow it,
 *   - every interior room furnished to capacity,
 *   - the venue furnished to capacity,
 *   - the neighbour population at its cap and walking.
 *
 * IT USES THE REAL PLACEMENT PATH. `place()` with `force`, never a private
 * shortcut, so auto-connect rebuilds, grid occupancy, per-instance state and
 * art construction all run exactly as they do for a kid — including the
 * `refreshConnected` cascade, which is the expensive part of a dense path grid
 * and the thing a synthetic fixture would silently skip.
 */

import { place } from '../build/placer'
import { engine } from '../core/engine'
import { save } from '../core/save'
import type { Cell, Rot } from '../core/grid'
import { allPlaceables, type AnyPlaceableDef } from '../core/placeable'
import { world, type SceneNode } from '../core/scene'
import type { PlaceableId, SceneId } from '../core/types'
import { ensurePopulation, MAX_NPCS, setPopulationCap } from '../npc/registry'
import { resetGovernor } from './governor'
import { resetSplit } from './index'

export interface StressOptions {
  /** Fraction of each scene's ground layer to cover. 1 = every legal cell. */
  townFill?: number
  /** Also stack a second level where the piece below allows it. */
  stack?: boolean
  /** Fill interior rooms and the venue too. */
  interiors?: boolean
  /** Put the neighbour population at its cap. */
  npcs?: boolean
  /** Deterministic layout seed. */
  seed?: number
}

export interface StressSceneResult {
  sceneId: string
  placed: number
  attempted: number
  cells: number
}

export interface StressReport {
  placed: number
  scenes: StressSceneResult[]
  npcs: number
  /** Wall-clock ms the whole build took — this is also the save-hydration cost. */
  buildMs: number
}

/**
 * Defs safe to carpet a scene with. Doors, stairs and windows are excluded: they
 * only place against a shell and would fail every attempt while burning time,
 * and a plot full of doors is not a shape any kid produces.
 */
const SKIP_CATEGORIES = new Set(['door', 'stair', 'window', 'roof'])

function fillDefsFor(node: SceneNode): AnyPlaceableDef[] {
  const scale = node.def.grid.scale
  return allPlaceables()
    .filter((d) => d.scale === scale || d.scale === 'both')
    .filter((d) => !SKIP_CATEGORIES.has(d.category))
    .filter((d) => d.footprint.w <= 2 && d.footprint.d <= 2)
    .sort((a, b) => (a.id < b.id ? -1 : 1))
}

function rng(seed: number): () => number {
  let s = seed >>> 0 || 1
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0
    return s / 0x100000000
  }
}

function fillScene(
  node: SceneNode,
  fill: number,
  stack: boolean,
  seed: number,
): StressSceneResult {
  const defs = fillDefsFor(node)
  const sceneId = node.def.id
  const spec = node.grid.spec
  const cells = spec.width * spec.depth
  const budget = Math.max(0, Math.round(cells * Math.min(1, Math.max(0, fill))))
  const r = rng(seed)
  let placed = 0
  let attempted = 0
  if (defs.length === 0) return { sceneId: sceneId as string, placed, attempted, cells }

  const levels = stack ? 2 : 1
  for (let gy = 0; gy < levels && placed < budget; gy++) {
    for (let gz = 0; gz < spec.depth && placed < budget; gz++) {
      for (let gx = 0; gx < spec.width && placed < budget; gx++) {
        // Deterministic def choice that still varies neighbour-to-neighbour, so
        // auto-connect masks and the material mix look like a real build rather
        // than one repeated prop three.js could cull as a block.
        const def = defs[(gx * 7 + gz * 13 + gy * 3 + Math.floor(r() * defs.length)) % defs.length]
        if (def === undefined) continue
        const anchor: Cell = { gx, gy, gz }
        const rot = ((gx + gz) % 4) as Rot
        attempted++
        const id = place(sceneId as SceneId, def.id as PlaceableId, anchor, rot, {
          force: true,
          silent: true,
        })
        if (id !== null) placed++
      }
    }
  }
  return { sceneId: sceneId as string, placed, attempted, cells }
}

/** Yield to the browser so a huge build cannot trip the watchdog or a hitch gate. */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()))
}

export async function buildStress(opts: StressOptions): Promise<StressReport> {
  const townFill = opts.townFill ?? 0.85
  const stack = opts.stack ?? true
  const interiors = opts.interiors ?? true
  const wantNpcs = opts.npcs ?? true
  const seed = opts.seed ?? 20260802

  const t0 = performance.now()
  const scenes: StressSceneResult[] = []

  let i = 0
  for (const id of world.ids()) {
    const node = world.require(id)
    const isExterior = node.def.kind === 'exterior'
    if (!isExterior && !interiors) continue
    scenes.push(fillScene(node, isExterior ? townFill : 1, stack, seed + i * 977))
    i++
    // One frame between scenes keeps a 1500-placement build from becoming a
    // single 20-second script task that the browser reports as one giant hitch.
    await nextFrame()
  }

  let npcs = 0
  if (wantNpcs) {
    setPopulationCap(MAX_NPCS)
    ensurePopulation(false)
    npcs = MAX_NPCS
  }

  const placed = scenes.reduce((a, s) => a + s.placed, 0)
  const buildMs = performance.now() - t0

  /*
   * MARK THE SAVE DIRTY. `place(..., { silent: true })` deliberately does not
   * call `save.request`, so a fixture built this way leaves the save clean and
   * `flush()` returns false without writing a byte — which is exactly what the
   * stress gate's "save write 0.1 ms, ok=false" was reporting. A save-timing
   * measurement that never writes is worse than none.
   */
  save.request('perf:stress')

  // The build itself is a legitimate multi-second hitch; measuring it as if it
  // were gameplay would fail every gate for the wrong reason.
  engine.resetPerf()
  resetGovernor()
  resetSplit()
  await nextFrame()

  return { placed, scenes, npcs, buildMs }
}
